import { useElementShown } from '@hh.ru/analytics-js';
import cardCourseElementShown from '@hh.ru/analytics-js-events/build/career_platform/courses/card_course_element_shown';
import { translation, TranslatedComponent } from '@hh.ru/front-static-app';
import { CheckableCard, Text } from '@hh.ru/magritte-ui';

import CardEdProgramBreadcrumbsInfo from 'src/components/CardEdProgram/common/CardEdProgramBreadcrumbsInfo';
import { cardEdProgramButtonLinkExternalHandler } from 'src/components/CardEdProgram/common/CardEdProgramButtonLinkExternal';
import CardEdProgramLogoPromoCompany from 'src/components/CardEdProgram/common/CardEdProgramLogoPromoCompany';
import { EdProgramDto, EdProgramCoursePurpose, isEdProgramTypeFollowLink } from 'src/types/EdProgram';
import getDataQa from 'src/utils/getDataQa';
import getKebabCase from 'src/utils/getKebabCase';
import pluralize from 'src/utils/pluralize';

import styles from './styles.less';

interface CardEdProgramSmallProps {
    edProgram: EdProgramDto;
}

const TrlKeys = {
    breadcrumbs: {
        profession: 'career-platform.components.card-course.breadcrumbs.profession',
        skill: 'career-platform.components.card-course.breadcrumbs.skill',
    },
    date: {
        SECOND: {
            one: 'career-platform.pluralize.second.one',
            some: 'career-platform.pluralize.second.some',
            many: 'career-platform.pluralize.second.many',
        },
        MINUTE: {
            one: 'career-platform.pluralize.minute.one',
            some: 'career-platform.pluralize.minute.some',
            many: 'career-platform.pluralize.minute.many',
        },
        HOURS: {
            one: 'career-platform.pluralize.hours.one',
            some: 'career-platform.pluralize.hours.some',
            many: 'career-platform.pluralize.hours.many',
        },
        DAYS: {
            one: 'career-platform.pluralize.days.one',
            some: 'career-platform.pluralize.days.some',
            many: 'career-platform.pluralize.days.many',
        },
        WEEKS: {
            one: 'career-platform.pluralize.weeks.one',
            some: 'career-platform.pluralize.weeks.some',
            many: 'career-platform.pluralize.weeks.many',
        },
        MONTHS: {
            one: 'career-platform.pluralize.months.one',
            some: 'career-platform.pluralize.months.some',
            many: 'career-platform.pluralize.months.many',
        },
        YEARS: {
            one: 'career-platform.pluralize.years.one',
            some: 'career-platform.pluralize.years.some',
            many: 'career-platform.pluralize.years.many',
        },
    },
};

const CardEdProgramSmall: TranslatedComponent<CardEdProgramSmallProps> = ({ edProgram, trls }) => {
    const isCourseByProfession = edProgram.purpose === EdProgramCoursePurpose.Profession;

    const getBreadcrumbsInfo = () => {
        const result = [isCourseByProfession ? trls[TrlKeys.breadcrumbs.profession] : trls[TrlKeys.breadcrumbs.skill]];

        if (edProgram.level) {
            const level = [edProgram.level.from, edProgram.level.to].filter(Boolean).join('→');
            result.push(level);
        }

        if (edProgram.duration) {
            result.push(
                pluralize({
                    count: edProgram.duration.value,
                    one: trls[TrlKeys.date[edProgram.duration.type].one],
                    some: trls[TrlKeys.date[edProgram.duration.type].some],
                    many: trls[TrlKeys.date[edProgram.duration.type].many],
                    useCount: true,
                })
            );
        }

        return result;
    };

    const handleClick = () => {
        if (isEdProgramTypeFollowLink(edProgram)) {
            cardEdProgramButtonLinkExternalHandler(edProgram);
        }
    };

    const ref = useElementShown(cardCourseElementShown, {
        courseName: edProgram.name,
        streamName: edProgram?.stream?.name,
        providerId: edProgram?.provider?.id || null,
        providerName: edProgram?.provider?.name || null,
        cardType: edProgram.purpose,
        professionId: edProgram.profession?.id || null,
        priceDetails: JSON.stringify(edProgram.priceDetails),
    });

    if (!isEdProgramTypeFollowLink(edProgram)) {
        return null;
    }

    return (
        <CheckableCard
            checked={false}
            readOnly
            key={edProgram.id}
            borderRadius={16}
            padding={16}
            toneHover
            type="checkbox"
            onClick={handleClick}
            labelDataQa={getDataQa('course', edProgram.id, getKebabCase(edProgram.purpose))}
        >
            <div ref={ref} className={styles.courseContent}>
                {edProgram.provider && (
                    <div className={styles.logo}>
                        <CardEdProgramLogoPromoCompany
                            name={edProgram.provider.name}
                            iconSrc={edProgram.provider.icon}
                        />
                    </div>
                )}
                <div className={styles.title}>
                    <Text typography="subtitle-2-semibold" data-qa={getDataQa('name')}>
                        {edProgram.name}
                    </Text>
                </div>
                <CardEdProgramBreadcrumbsInfo items={getBreadcrumbsInfo()} />
            </div>
        </CheckableCard>
    );
};

export default translation(CardEdProgramSmall);
